/**
 * Whole-year tick placement for the charts' time axis.
 *
 * Chart points carry FRACTIONAL years (`monthIndex / 12`), so a Recharts category axis places
 * ticks on whatever data points happen to fit and every label then rounds into the same handful
 * of years: `0y, 1y, 1y, 1y, 2y, 2y…`. Ticks have to be chosen explicitly, on a numeric axis.
 *
 * Lives here rather than beside the charts because it is real logic with boundaries worth
 * testing, and `src/components/charts/**` is excluded from the coverage gate.
 */

/** Upper bound on tick count. Nine 11px labels still read cleanly on a narrow chart card. */
const MAX_TICKS = 9;

/** Tick spacings tried in order; the first that fits within MAX_TICKS wins. */
const STEPS: readonly number[] = [1, 2, 5, 10];

/** Widest spacing, used past the point where even that exceeds MAX_TICKS. */
const WIDEST_STEP = 10;

/**
 * The horizon a series covers, in whole years.
 *
 * The projection loop runs `m = 0; m < years * 12`, so the last point of a 10-year run sits at
 * month 119, i.e. 9.92 years, not 10. Rounding recovers the horizon the user actually asked for;
 * the resulting sub-month gap at the right edge is under 1% of the axis width.
 */
export function axisEndYear(maxYear: number): number {
  if (!Number.isFinite(maxYear)) return 1;
  return Math.max(1, Math.round(maxYear));
}

/**
 * Whole-year tick positions spanning `0..axisEndYear(maxYear)`.
 *
 * The end year is always included, even when it does not sit on the step (a 9-year horizon
 * gives `0, 2, 4, 6, 8, 9`), because the end of the horizon is the one year a reader looks for.
 */
export function yearTicks(maxYear: number): number[] {
  const end = axisEndYear(maxYear);
  const step = STEPS.find((s) => end / s <= MAX_TICKS - 1) ?? WIDEST_STEP;
  const ticks: number[] = [];
  for (let y = 0; y < end; y += step) ticks.push(y);
  ticks.push(end);
  return ticks;
}

/** Format a whole-year tick. Kept beside the tick maths so the two cannot drift apart. */
export function formatYearTick(year: number): string {
  return `${Math.round(year)}y`;
}

/** Largest `year` in a chart series. Points are built in month order, so the last one is the max. */
export function maxYearOf(data: readonly { year?: number }[] | undefined): number {
  const last = data?.[data.length - 1]?.year;
  return typeof last === 'number' && Number.isFinite(last) ? last : 1;
}
